import React, { useEffect, useState, useCallback } from 'react';
import { Divider, Drawer, List, Typography, Box, ListItemButton, Tooltip, Chip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from 'services/context/UserContext';
import { useFavorites } from 'services/context/UseFavorites';
import { useSharedViews } from 'services/context/useSharedViews';
// import { useAppContext } from 'services/context/AppContext';
import { getClientLogoV2, getBaseDocUrl } from 'services/api/ClientHandler';
import { convertUrlToAbsolute, getDatePreview } from 'services/helpers/dateParser';
import ContainerColumn from 'components/v2/layout/ContainerColumn';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RecyclingIcon from '@mui/icons-material/Recycling';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TransformIcon from '@mui/icons-material/Transform';
import ViewListIcon from '@mui/icons-material/ViewList';
import RestoreIcon from '@mui/icons-material/Restore';
import MailIcon from '@mui/icons-material/Mail';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StoreIcon from '@mui/icons-material/Store';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import RoundedPlusIcon from 'components/common/icons/RoundedPlusIcon.js';
import ContainerRow from 'components/v2/layout/ContainerRow.js';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from 'components/common/icons/EditIcon';
import StarIcon from 'components/common/icons/StarIcon.js'
import { ROUTE_CONFIG } from 'config/routes.config';
import { hasAnyRole } from 'config/roles';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        ...theme.menu,
        ...theme.center,
        width: 'clamp(280px, 19vw, 350px)', // Keep the width
        height: '100vh', // Ensure the menu takes the full height of the viewport
        position: 'fixed', // Fix the menu to the viewport
        top: 0, // Align to the top of the screen
        left: 0, // Align to the left of the screen
        overflow: 'hidden', // Disable scrolling
    },
}));

const CloseIconStyled = styled(CloseIcon)(() => ({
    cursor: 'pointer',
    position: 'absolute',
    top: '-8px', // Adjusted to move closer to the top
    right: '0px', // Adjusted to move closer to the right
}));

const MenuHeader = styled(Box)(() => ({
    margin: '1em 1em 0.8em 1em',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative', // Allow positioning of the close icon
    maxWidth: 'clamp(260px, 19vw, 340px)',
}));

const MenuList = styled(List)(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '85%',
    margin: '0em 1em 0em 1em',
    width: '80%',
    overflowY: 'auto',
    overflowX: 'hidden',
}));

const ImageContainer = styled('div')(({ theme }) => ({
    ...theme.center,
    marginBottom: '0.5em',
    marginLeft: '-1.5em',
    minHeight: '2.7em',
}));

const ListItemButtonLight = styled(ListItemButton)(({ theme }) => ({
    padding: '0.1em 1em',
    width: 'fit-content',
    borderRadius: '0.5em',
    fontSize: theme.typography.secondary.fontSize,
    color : theme.palette.secondary.main
}));

const CustomDivider = styled(Divider)(({ theme }) => ({
    ...theme.divider,
    width: '14vw',
    borderColor: '#E5E5E5',
    borderWidth: 1,
    opacity: 1,
}));

const MenuItem = styled(ListItemButton)(({ theme, isActive, isFavorite, color, mrg }) => ({
    borderRadius: '0.5em',
    padding: isFavorite ? '0.1em 1em' : mrg ? `0.4em ${mrg}` : '0.3em 0.7em',
    color: isActive ? '#0072E4' : color ? color : theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));


const SubSectionItem = styled(ListItemButton)(({ isActive }) => ({
    fontWeight: 400,
    letterSpacing: '-0.15px',
    lineHeight: '100%',
    fontSize: 'clamp(5px, 1vw + 0.8px, 13px)',
    padding: '0.3em 0.4em',
    color: isActive ? '#0072E4' : 'black',
}));

const ItemText = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif', 
    fontWeight: 550,
    fontSize: 'clamp(8px, 1vw + 1.5px, 14px)',
    paddingLeft: '0.7em',
    maxWidth: 'clamp(140px, 10vw, 200px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '18px',
    
}));

const FavoritIcon = styled(RoundedPlusIcon)(() => ({
    marginBottom: '4em',
    size: '0.9vw',
}));

const Section = styled(Box)(() => ({
    margin: '1em 0em 0em 0em',
    display: 'flex',
    flexDirection: 'column',
}));

const MenuSectionTitle = styled(Typography)(() => ({
    fontWeight: 600,
    fontSize: 'clamp(5px, 1vw + 0.8px, 13px)',
    color: '#787D8A',
    lineHeight: 1,
}));

const MenuSectionClickableTitle = styled(Typography)(() => ({
    fontWeight: 600,
    fontSize: 'clamp(5px, 1vw + 0.8px, 13px)',
    color: '#787D8A',
    lineHeight: 1,
}));

const SectionBody = styled(ContainerColumn)(() => ({
    margin: '0.5em 0em 0em 0em',
}));

const MenuFooter = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: 'fit-content',
    margin: '1.5em 0em 1em 0em',
}));


const ContainerIcon = styled ('div' )(() => ({
    display: 'flex',
    justifyContent :' center',
    alignItems :'center',
   padding : '0.3em',

   '&:hover' :{
    backgroundColor : '#E7E7E7'
   },
   borderRadius : '0.5em'
}));

const StyledChip = styled(Chip)(({theme}) => ({
  ...theme.typography.body3,
  lineHeight: 5,
  minWidth: 'auto',
  height: 20,
  marginTop:3,
  marginInline:1,
  borderRadius: 20,
  color: '#5b5959ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 4px',
  '& .MuiChip-label': {
    padding: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
}));

/**
 * Presentation du menu (icone + description tooltip) par id de route.
 * La structure, les roles, les chemins et la version viennent de ROUTE_CONFIG.
 * Ici on ne gere que l'affichage.
 */
const MENU_META = {
  'store-manager': { icon: StoreIcon, description: 'Vue responsable magasin : actions, contenant, inventaire et performances' },
  'my-region': { icon: LocationOnIcon, description: 'Pilotage régional : suivi des magasins, alertes et indicateurs' },
  'my-warehouse': { icon: WarehouseIcon, description: "Pilotage opérationnel de l'entrepôt : stock, expéditions et anomalies" },
  decision: { icon: AutoGraphIcon, description: "Vue d'ensemble de votre flotte avec indicateurs clés et graphiques de tendance" },
  industrial: { icon: QueryStatsIcon, description: 'Vue de pilotage production et approvisionnement' },
  operation: { icon: TransformIcon, description: "Analysez en détail la répartition et l'état de vos emballages par site" },
  compliance: { icon: RecyclingIcon, description: "Étude d'impact globale de votre flotte réemployable" },
  historic: { icon: RestoreIcon, description: 'Accédez aux données brutes : scans et événements sur 12 mois glissants' },
  'my-orders': { icon: LocalShippingIcon, description: 'Consultez et filtrez toutes vos commandes et expéditions' },
  'admin-carrier-perf': { icon: LocalShippingIcon },
  'admin-exceptions': { icon: AdminPanelSettingsIcon },
  'admin-mail-supervision': { icon: MailIcon },
  'admin-qrcode': { icon: AdminPanelSettingsIcon },
};

/** Items visibles d'une section donnee, filtres par roles utilisateur. */
const menuItemsForSection = (userRoles, sections) =>
  ROUTE_CONFIG.filter(
    (r) =>
      r.menu?.show &&
      sections.includes(r.menu.section) &&
      hasAnyRole(userRoles, r.roles),
  );

export default function Menu({ open, setOpen }) {
    const user = useUser();
    const userRoles = user.user?.roles || [];
    const [urlSrcLogo, setUrlSrcLogo] = useState(null);
    const { favorites, addFavorite, removeFavorite, updateFavoriteLabel } = useFavorites();
    const { sharedViews, addSharedView, removeSharedView, updateSharedViewLabel, canEdit, isLoading: isLoadingShared } = useSharedViews(user.user);
    // const { openTutorial } = useAppContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [editingPath, setEditingPath] = useState(null);
    const [newLabel, setNewLabel] = useState('');
    const [editingSharedPath, setEditingSharedPath] = useState(null);
    const [newSharedLabel, setNewSharedLabel] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [overwriteDialog, setOverwriteDialog] = useState({ open: false, label: '' });
    const isActive = (path) => {
        return location.pathname === path;
    };
    const [isOpen, setIsOpen] = useState(false);
    const toggleSection = () => setIsOpen(!isOpen);
  
    const fetchClientLogoV2 = useCallback(async () => {
        try {
            const res = await getClientLogoV2(user?.user?.id);
            if (res.status === 200 && res.data?.url) {
                const imgSrc = res.data.url;
                setUrlSrcLogo(imgSrc);
            } else if (res.status === 404) {
                // Client doesn't have a custom logo, use default
                setUrlSrcLogo(null);
            } else {
                throw new Error("Error while fetching client logo. Code: " + res.status);
            }
        } catch (error) {
            // Silently handle errors - will use default logo
            if (error.message && !error.message.includes('404')) {
                console.error("Error fetching client logo:", error);
            }
            setUrlSrcLogo(null);
        }
    }, [user]);

    useEffect(() => {
        fetchClientLogoV2();
    }, [fetchClientLogoV2]);

    return (
        <StyledDrawer
            anchor="left"
            open={open}
            onClose={() => setOpen(false)}
        >
            <MenuHeader>
                <CloseIconStyled onClick={() => setOpen(false)} />
                <ImageContainer>
                    <img
                        src={urlSrcLogo
                            ? urlSrcLogo
                            : "/MOBIUSPACK.png"}
                        alt="Logo"
                        style={{ maxWidth: '60%' }}
                    />
                </ImageContainer>
                <Tooltip title="mon profil" arrow>
                    <ListItemButtonLight onClick={() => { navigate("/profile") }} variant="body2" color="textSecondary">{user?.user?.email}</ListItemButtonLight>
                </Tooltip>
                <Box sx={{display:'flex', flexDirection:'row', flexWrap: 'wrap', maxWidth: 'clamp(200px, 15vw, 280px)', justifyContent:'center', alignContent:'center'}}>
                {user.user.roles && user.user.roles.map((r, idx) => (
                    <StyledChip key={idx} label={r}/>
                ))}
                </Box>
            </MenuHeader>
            <CustomDivider />
            <MenuList>
                <ContainerColumn>
                    <MenuItem isActive={isActive('/notifications')} onClick={() => navigate('/notifications')}>
                        <NotificationsActiveIcon strokeColor={isActive('/notifications') ? '#0072E4' : 'black'} size='1.2vw' />
                        <ItemText> Notifications </ItemText>
                    </MenuItem>
                    <Section>
                        <MenuSectionTitle>Tableau de bord</MenuSectionTitle>
                        <SectionBody>
                            {menuItemsForSection(userRoles, ['main', 'partner']).map((route) => {
                                const path = `/${route.path}`;
                                const Icon = MENU_META[route.id]?.icon;
                                const description = MENU_META[route.id]?.description;
                                return (
                                <Tooltip key={route.id} title={description || route.menu.label} arrow placement="right">
                                    <MenuItem
                                    isActive={isActive(path)}
                                    onClick={() => {
                                        navigate(path);
                                        setOpen(false); // Ferme le menu
                                    }}
                                    >
                                    {Icon && (
                                        <Icon
                                        strokeColor={isActive(path) ? '#0072E4' : 'black'}
                                        size='1.1vw'
                                        />
                                    )}
                                    <ItemText>{route.menu.label}</ItemText>
                                    </MenuItem>
                                </Tooltip>
                                );
                            })}
                        </SectionBody>
                    </Section>
                    <Section>
                        <Tooltip title="Cliquer pour ajouter la page actuelle aux favoris" arrow placement="right">
                            <Box 
                                onClick={() => addFavorite(`${new Date().toISOString().replace('T', ' ').replace(/[:.]/g, '-').slice(0, 19)}`)} 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.5em',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.7 }
                                }}
                            >
                                <MenuSectionClickableTitle>Mes favoris</MenuSectionClickableTitle>
                                <FavoritIcon />
                            </Box>
                        </Tooltip>
                        <SectionBody>
                            {favorites.map(({ path, label }) => (
                            <Tooltip key={path} title={`${label} : ${window.location.origin}${path}`} arrow placement="right">
                                <MenuItem
                                isFavorite = {true}
                                isActive={isActive(path)}
                                onClick={() => { if (!editingPath) {
                                    navigate(path);
                                    setOpen(false); // Ferme le menu
                                }  }}
                                style={{
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                                >
                                <ContainerRow gap="0.5em" justifyContent="space-between" style={{ alignItems: 'center', flex: 1 }}>
                                    
                                    { editingPath === path ? (
                                    
                                    <ContainerRow>
                                    <StarIcon size="1.1vw" />
                                    <input
                                        type="text"
                                        value={newLabel}
                                        placeholder={label}
                                        onChange={(e) => setNewLabel(e.target.value)}
                                        onBlur={() => {
                                            if (newLabel.trim() !== '') {
                                                updateFavoriteLabel(path, newLabel);
                                            }
                                            setEditingPath(null); // ferme le champ
                                            }}
                                        autoFocus
                                        style={{
                                        fontSize: 'clamp(12px, 1vw, 16px)',
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        maxWidth : '12vw',
                                        paddingLeft: '0.7em',
                                        }}
                                    />
                                    </ContainerRow>
                                    ) : (
                                <>
                                <ContainerRow>
                                    <StarIcon strokeColor={isActive(path) ? '#0072E4' : 'black'} size="1.1vw" />
                                    <ItemText>{label}</ItemText>
                                </ContainerRow>
                                    <ContainerRow>
                                    <ContainerIcon
                                    onClick={(e) => {
                                        e.preventDefault(); 
                                        e.stopPropagation();
                                        setEditingPath(path);
                                        setNewLabel(label);
                                        }}
                                    >
                                        <EditIcon
                                            size='0.8vw'
                                            strokeColor={isActive(path) ? '#0072E4' : 'black'}
                                        />
                                    </ContainerIcon>
                                    <ContainerIcon>
                                        <CloseIcon
                                            onClick={(e) => {
                                            e.stopPropagation();
                                            removeFavorite(path);
                                            }}
                                            sx={{ fontSize: 'clamp(12px, 1vw, 16px)', cursor: 'pointer' }}
                                        />  
                                    </ContainerIcon>
                                    </ContainerRow>
                                </>
                                    )}
                                </ContainerRow>
                                </MenuItem>
                            </Tooltip>
                            ))}

                        </SectionBody>
                    </Section>
                    
                    {/* Section Vues partagées */}
                    <Section>
                        {canEdit && (
                            <Tooltip title="Cliquer pour ajouter la page actuelle aux vues partagées" arrow placement="right">
                                <Box
                                    onClick={async () => {
                                        const label = new Date().toISOString().replace('T', ' ').replace(/[:.]/g, '-').slice(0, 19);
                                        const result = await addSharedView(label);
                                        if (result?.conflict) {
                                            setOverwriteDialog({ open: true, label });
                                        } else if (result) {
                                            setSnackbar({
                                                open: true,
                                                message: result.message,
                                                severity: result.success ? 'success' : 'error'
                                            });
                                        }
                                    }}
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.5em',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.7 }
                                    }}
                                >
                                    <MenuSectionClickableTitle>Vues partagées</MenuSectionClickableTitle>
                                    <ViewListIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 18px)' }} />
                                </Box>
                            </Tooltip>
                        )}
                        {!canEdit && (
                            <Tooltip title="Vues partagées de l'organisation (lecture seule)" arrow placement="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                    <MenuSectionClickableTitle>Vues partagées</MenuSectionClickableTitle>
                                </Box>
                            </Tooltip>
                        )}
                        <SectionBody>
                            {isLoadingShared && (
                                <Typography variant="caption" color="text.secondary" sx={{ pl: 2, py: 1 }}>
                                    Chargement...
                                </Typography>
                            )}
                            {!isLoadingShared && sharedViews.map(({ path, label, isDynamic, createdBy, createdByName }) => {
                                const datePreview = isDynamic ? getDatePreview(path) : null;
                                const tooltipContent = (
                                    <>
                                        <div><strong>{label}</strong></div>
                                        {isDynamic && datePreview && (
                                            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
                                                🕒 Dynamique: {datePreview}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
                                            Par: {createdByName || createdBy}
                                        </div>
                                        <div style={{ fontSize: '0.75em', color: '#ccc', marginTop: '2px' }}>
                                            {window.location.origin}{path}
                                        </div>
                                    </>
                                );
                                
                                return (
                                    <Tooltip key={path} title={tooltipContent} arrow placement="right" slotProps={{ popper: { style: { zIndex: 1500 } } }}>
                                        <MenuItem
                                            isFavorite={true}
                                            isActive={isActive(path)}
                                            onClick={() => {
                                                if (!editingSharedPath) {
                                                    const absoluteUrl = convertUrlToAbsolute(path);
                                                    const [pathname, search] = absoluteUrl.split('?');
                                                    navigate({
                                                        pathname,
                                                        search: search ? `?${search}` : '',
                                                    });
                                                    setOpen(false);
                                                }
                                            }}
                                            style={{
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <ContainerRow gap="0.5em" justifyContent="space-between" style={{ alignItems: 'center', flex: 1 }}>
                                                {editingSharedPath === path ? (
                                                    <ContainerRow>
                                                        <StarIcon size="1.1vw" />
                                                        <input
                                                            type="text"
                                                            value={newSharedLabel}
                                                            placeholder={label}
                                                            onChange={(e) => setNewSharedLabel(e.target.value)}
                                                            onBlur={async () => {
                                                                if (newSharedLabel.trim() !== '') {
                                                                    const result = await updateSharedViewLabel(path, newSharedLabel);
                                                                    if (result) {
                                                                        setSnackbar({ 
                                                                            open: true, 
                                                                            message: result.message, 
                                                                            severity: result.success ? 'success' : 'error' 
                                                                        });
                                                                    }
                                                                }
                                                                setEditingSharedPath(null);
                                                            }}
                                                            autoFocus
                                                            style={{
                                                                fontSize: 'clamp(12px, 1vw, 16px)',
                                                                border: 'none',
                                                                outline: 'none',
                                                                background: 'transparent',
                                                                maxWidth: '12vw',
                                                                paddingLeft: '0.7em',
                                                            }}
                                                        />
                                                    </ContainerRow>
                                                ) : (
                                                    <>
                                                        <ContainerRow gap="0.3em" alignItems="center">
                                                            <StarIcon strokeColor={isActive(path) ? '#0072E4' : 'black'} size="1.1vw" />
                                                            <ItemText>{label}</ItemText>
                                                        </ContainerRow>
                                                        <ContainerRow>
                                                            <ContainerIcon
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    if (canEdit) {
                                                                        setEditingSharedPath(path);
                                                                        setNewSharedLabel(label);
                                                                    } else {
                                                                        setSnackbar({ 
                                                                            open: true, 
                                                                            message: 'Permission refusée', 
                                                                            severity: 'warning' 
                                                                        });
                                                                    }
                                                                }}
                                                                style={{ 
                                                                    cursor: canEdit ? 'pointer' : 'not-allowed',
                                                                    opacity: canEdit ? 1 : 0.5
                                                                }}
                                                            >
                                                                <EditIcon
                                                                    size='0.8vw'
                                                                    strokeColor={isActive(path) ? '#0072E4' : 'black'}
                                                                />
                                                            </ContainerIcon>
                                                            <ContainerIcon
                                                                style={{ 
                                                                    cursor: canEdit ? 'pointer' : 'not-allowed',
                                                                    opacity: canEdit ? 1 : 0.5
                                                                }}
                                                            >
                                                                <CloseIcon
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        if (canEdit) {
                                                                            const result = await removeSharedView(path);
                                                                            if (result) {
                                                                                setSnackbar({ 
                                                                                    open: true, 
                                                                                    message: result.message, 
                                                                                    severity: result.success ? 'success' : 'error' 
                                                                                });
                                                                            }
                                                                        } else {
                                                                            setSnackbar({ 
                                                                                open: true, 
                                                                                message: 'Permission refusée', 
                                                                                severity: 'warning' 
                                                                            });
                                                                        }
                                                                    }}
                                                                    sx={{ fontSize: 'clamp(12px, 1vw, 16px)' }}
                                                                />
                                                            </ContainerIcon>
                                                        </ContainerRow>
                                                    </>
                                                )}
                                            </ContainerRow>
                                        </MenuItem>
                                    </Tooltip>
                                );
                            })}
                        </SectionBody>
                    </Section>
                    
                    {menuItemsForSection(userRoles, ['admin']).length > 0 && (
                        <Section>
                            <MenuSectionTitle onClick={toggleSection} style={{ cursor: 'pointer' }}>
                                Espace admin {isOpen ? '▾' : '▸'}
                            </MenuSectionTitle>
                            {isOpen && (
                                <SectionBody>
                                {menuItemsForSection(userRoles, ['admin']).map((route) => {
                                    const path = `/${route.path}`;
                                    const Icon = MENU_META[route.id]?.icon;
                                    return (
                                    <MenuItem
                                        key={route.id}
                                        isActive={isActive(path)}
                                        onClick={() => navigate(path)}
                                    >
                                        {Icon && (
                                            <Icon
                                            strokeColor={isActive(path) ? '#0072E4' : 'black'}
                                            size="1.1vw"
                                            />
                                        )}
                                        <ItemText>{route.menu.label}</ItemText>
                                    </MenuItem>
                                    );
                                })}
                                </SectionBody>
                            )}
                            </Section>
                        )}
                </ContainerColumn>
                <Section>
                    {menuItemsForSection(userRoles, ['settings']).map((route) => {
                        const path = `/${route.path}`;
                        return (
                            <SubSectionItem
                                key={route.id}
                                isActive={isActive(path)}
                                onClick={() => navigate(path)}
                            >
                                {' '}{route.menu.label}{' '}
                            </SubSectionItem>
                        );
                    })}
                    <SubSectionItem onClick={() => window.open(getBaseDocUrl(), "_blank")}> Documentation </SubSectionItem>
                    {/* <SubSectionItem onClick={() => {
                        openTutorial();
                        setOpen(false);
                    }}> Tutoriel </SubSectionItem> */}
                </Section>
            </MenuList>
            <CustomDivider />
            <MenuFooter>
                <img src="/MOBIUSPACK.png" alt="Logo" style={{ maxWidth: '25%' }} />
                <Typography variant="serialC"> Version v2.3.0 </Typography>
            </MenuFooter>
            
            {/* Dialog de confirmation écrasement vue partagée */}
            <Dialog
                open={overwriteDialog.open}
                onClose={() => setOverwriteDialog({ open: false, label: '' })}
            >
                <DialogTitle>Vue existante</DialogTitle>
                <DialogContent>
                    <Typography>
                        Une vue partagée avec le nom &quot;{overwriteDialog.label}&quot; existe déjà. Voulez-vous la remplacer ?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverwriteDialog({ open: false, label: '' })}>
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            const result = await addSharedView(overwriteDialog.label, true);
                            setOverwriteDialog({ open: false, label: '' });
                            if (result) {
                                setSnackbar({
                                    open: true,
                                    message: result.message,
                                    severity: result.success ? 'success' : 'error'
                                });
                            }
                        }}
                    >
                        Remplacer
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar pour feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </StyledDrawer>
    );
};


